import { ContractStatus, ContractType, ContractStatusConfig } from '@/types';

/**
 * Contract Status Management Utilities
 * Integrated with Slovak and German compliance systems
 */

// Contract Status Configurations
export const CONTRACT_STATUS_CONFIGS: Record<ContractStatus, {
  label: string;
  description: string;
  bgColor: string;
  textColor: string;
  payrollEligible: boolean;
  canWorkShifts: boolean;
}> = {
  draft: {
    label: 'Draft',
    description: 'Contract being prepared, not yet active',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    payrollEligible: false,
    canWorkShifts: false,
  },
  active: {
    label: 'Active',
    description: 'Active employment contract',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
    payrollEligible: true,
    canWorkShifts: true,
  },
  suspended: {
    label: 'Suspended',
    description: 'Contract temporarily suspended',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    payrollEligible: false,
    canWorkShifts: false,
  },
  terminated: {
    label: 'Terminated',
    description: 'Contract terminated',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
    payrollEligible: false,
    canWorkShifts: false,
  },
  expired: {
    label: 'Expired',
    description: 'Fixed-term contract expired',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
    payrollEligible: false,
    canWorkShifts: false,
  },
};

// Valid status transitions by current status
export const CONTRACT_STATUS_TRANSITIONS: Record<ContractStatus, ContractStatus[]> = {
  draft: ['active', 'terminated'],
  active: ['suspended', 'terminated', 'expired'],
  suspended: ['active', 'terminated'],
  terminated: [], // No transitions from terminated
  expired: [], // No transitions from expired
};

// Contract type display names
export const CONTRACT_TYPE_DISPLAY_NAMES: Record<ContractType, { en: string; local: string }> = {
  // Slovak contract types
  TPP: { en: 'Permanent Employment', local: 'Trvalý pracovný pomer' },
  HALF_TIME: { en: 'Part-time Employment', local: 'Polovičný úväzok' },
  DoVP: { en: 'Work Performance Agreement', local: 'Dohoda o vykonaní práce' },
  DoPČ: { en: 'Work Activity Agreement', local: 'Dohoda o pracovnej činnosti' },
  DoBPŠ: { en: 'Student Work Agreement', local: 'Dohoda o brigádnickej práci študentov' },
  // German contract types
  VOLLZEIT: { en: 'Full-time Employment', local: 'Vollzeitbeschäftigung' },
  TEILZEIT: { en: 'Part-time Employment', local: 'Teilzeitbeschäftigung' },
  MINIJOB: { en: 'Mini-job (≤€520)', local: 'Minijob (≤€520)' },
  MIDIJOB: { en: 'Midi-job (€520-€2000)', local: 'Midijob (€520-€2000)' },
};

/**
 * Get contract status configuration
 */
export const getContractStatusConfig = (status: ContractStatus) => {
  return CONTRACT_STATUS_CONFIGS[status] || CONTRACT_STATUS_CONFIGS.active;
};

/**
 * Get valid status transitions for current status
 */
export const getValidContractStatusTransitions = (currentStatus: ContractStatus): ContractStatus[] => {
  return CONTRACT_STATUS_TRANSITIONS[currentStatus] || [];
};

/**
 * Format contract status for display
 */
export const formatContractStatus = (status: ContractStatus): string => {
  const config = getContractStatusConfig(status);
  return config.label;
};

/**
 * Check if status change requires a reason
 */
export const contractStatusRequiresReason = (status: ContractStatus): boolean => {
  return ['suspended', 'terminated'].includes(status);
};

/**
 * Check if contract is payroll eligible based on status and type
 */
export const isPayrollEligible = (
  contractStatus: ContractStatus,
  contractType?: ContractType
): boolean => {
  const statusConfig = getContractStatusConfig(contractStatus);
  return statusConfig.payrollEligible;
};

/**
 * Check if employee can work shifts based on contract status
 */
export const canWorkShifts = (
  contractStatus: ContractStatus,
  contractType?: ContractType
): boolean => {
  const statusConfig = getContractStatusConfig(contractStatus);
  return statusConfig.canWorkShifts;
};

/**
 * Get contract type display name
 */
export const getContractTypeDisplayName = (
  contractType: ContractType,
  language: 'en' | 'local' = 'en'
): string => {
  const displayNames = CONTRACT_TYPE_DISPLAY_NAMES[contractType];
  if (!displayNames) return contractType;
  return displayNames[language];
};

/**
 * Check if contract type is Slovak
 */
export const isSlovakContractType = (contractType: ContractType): boolean => {
  return ['TPP', 'HALF_TIME', 'DoVP', 'DoPČ', 'DoBPŠ'].includes(contractType);
};

/**
 * Check if contract type is German
 */
export const isGermanContractType = (contractType: ContractType): boolean => {
  return ['VOLLZEIT', 'TEILZEIT', 'MINIJOB', 'MIDIJOB'].includes(contractType);
};

/**
 * Get country code from contract type
 */
export const getCountryFromContractType = (contractType: ContractType): 'SK' | 'DE' | 'UNKNOWN' => {
  if (isSlovakContractType(contractType)) return 'SK';
  if (isGermanContractType(contractType)) return 'DE';
  return 'UNKNOWN';
};

/**
 * Validate contract status change
 */
export const validateContractStatusChange = (
  currentStatus: ContractStatus,
  newStatus: ContractStatus,
  contractType?: ContractType
): { valid: boolean; errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Check if transition is allowed
  const validTransitions = getValidContractStatusTransitions(currentStatus);
  if (!validTransitions.includes(newStatus)) {
    errors.push(`Cannot change from "${formatContractStatus(currentStatus)}" to "${formatContractStatus(newStatus)}"`);
  }
  
  // Add warnings for specific transitions
  if (newStatus === 'terminated' && currentStatus === 'active') {
    warnings.push('Ensure proper termination procedures are followed and notice period is respected');
  }
  
  if (newStatus === 'suspended' && contractType && ['DoVP', 'DoPČ', 'DoBPŠ'].includes(contractType)) {
    warnings.push('Suspension of agreement-based contracts may have different legal implications');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Get required fields for contract status based on type
 */
export const getRequiredFieldsForContractStatus = (
  status: ContractStatus,
  contractType?: ContractType,
  country: 'SK' | 'DE' = 'SK'
): string[] => {
  const baseRequiredFields: string[] = [];
  
  if (contractStatusRequiresReason(status)) {
    baseRequiredFields.push('contract_status_reason');
  }
  
  if (status === 'active' && contractType) {
    if (country === 'SK') {
      switch (contractType as any) {
        case 'TPP':
        case 'HALF_TIME':
          baseRequiredFields.push('rodne_cislo', 'contract_start_date', 'health_insurance_company', 'iban');
          break;
        case 'DoVP':
        case 'DoBPŠ':
          baseRequiredFields.push('rodne_cislo', 'contract_start_date');
          break;
        case 'DoPČ':
          baseRequiredFields.push('rodne_cislo', 'contract_start_date', 'health_insurance_company');
          break;
      }
    } else if (country === 'DE') {
      switch (contractType as any) {
        case 'VOLLZEIT':
        case 'TEILZEIT':
        case 'MIDIJOB':
          baseRequiredFields.push('steuer_id', 'contract_start_date', 'iban');
          break;
        case 'MINIJOB':
          baseRequiredFields.push('steuer_id', 'contract_start_date');
          break;
      }
    }
  }
  
  return baseRequiredFields;
};

/**
 * Get recommended notice period for termination
 */
export const getRecommendedNoticePeriod = (
  contractType: ContractType,
  employmentDurationMonths: number = 0
): { min: number; max: number; recommended: number } => {
  const country = getCountryFromContractType(contractType);
  
  if (country === 'SK') {
    switch (contractType as any) {
      case 'TPP':
        if (employmentDurationMonths < 12) return { min: 30, max: 60, recommended: 30 };
        if (employmentDurationMonths < 24) return { min: 30, max: 60, recommended: 45 };
        return { min: 30, max: 60, recommended: 60 };
      case 'HALF_TIME':
        return { min: 15, max: 30, recommended: 30 };
      case 'DoVP':
      case 'DoBPŠ':
        return { min: 0, max: 7, recommended: 0 };
      case 'DoPČ':
        return { min: 7, max: 14, recommended: 14 };
    }
  } else if (country === 'DE') {
    switch (contractType as any) {
      case 'VOLLZEIT':
      case 'TEILZEIT':
        if (employmentDurationMonths < 24) return { min: 30, max: 90, recommended: 30 };
        return { min: 30, max: 90, recommended: 60 };
      case 'MIDIJOB':
        return { min: 30, max: 30, recommended: 30 };
      case 'MINIJOB':
        return { min: 14, max: 14, recommended: 14 };
    }
  }
  
  // Default fallback
  return { min: 30, max: 60, recommended: 30 };
};

/**
 * Check if employee needs compliance validation for status change
 */
export const needsComplianceValidation = (
  contractType?: ContractType,
  newStatus?: ContractStatus
): boolean => {
  if (!contractType || !newStatus) return false;
  
  // Slovak and German contracts always need compliance validation for status changes
  return isSlovakContractType(contractType) || isGermanContractType(contractType);
};